import { useRef, useState, useEffect } from 'react';
import { X, Save, Send } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';
import ActionButton from '../ActionButton';
import ModalPortal from '../ModalPortal';

/**
 * EditFactureInterneModal - Modale d'édition d'une facture interne
 * Champs identiques à la modale de création : date, destinataire, lignes, TVA.
 */
function EditFactureInterneModal({
  isOpen,
  onClose,
  facture,
  onSave,
  onChange,
  onStatusChange,
  prestataires
}) {
  const mouseDownOnOverlay = useRef(false);

  // Lignes de détail — état local, synchro montantHT via onChange
  const [lines, setLines] = useState([{ description: '', montant: 0 }]);

  // Réinitialiser les lignes quand la facture change (ouverture modal)
  // Priorité aux lignes chargées depuis la DB (facture.lines), sinon fallback montant HT
  useEffect(() => {
    if (facture?.id) {
      if (facture.lines && facture.lines.length > 0) {
        setLines(facture.lines);
      } else {
        setLines([{
          description: facture.intitule || '',
          montant: facture.montantHT || 0
        }]);
      }
    }
  }, [facture?.id]);

  // Synchroniser les lignes et le total HT vers le reducer
  useEffect(() => {
    if (!isOpen) return;
    const total = lines.reduce((sum, l) => sum + (parseFloat(l.montant) || 0), 0);
    onChange('montantHT', total);
    onChange('lines', lines);
  }, [lines, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !facture) return null;

  const tauxTVA = facture.tauxTVA || 0;
  const montantHT = lines.reduce((sum, l) => sum + (parseFloat(l.montant) || 0), 0);
  const montantTVA = montantHT * (tauxTVA / 100);
  const montantTTC = montantHT + montantTVA;

  const handleAddLine = () => setLines(prev => [...prev, { description: '', montant: '' }]);
  const handleRemoveLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const handleLineChange = (i, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  return (
    <ModalPortal>
      <style>{`
        .edit-modal-scroll::-webkit-scrollbar { width: 6px; }
        .edit-modal-scroll::-webkit-scrollbar-track {
          background: ${colors.background.light};
          border-radius: 3px;
          margin: 0.5rem 0;
        }
        .edit-modal-scroll::-webkit-scrollbar-thumb {
          background: ${colors.secondary.lightTeal};
          border-radius: 3px;
        }
        .edit-modal-scroll::-webkit-scrollbar-thumb:hover { background: ${colors.primary.teal}; }
        .edit-modal-scroll { scrollbar-width: thin; scrollbar-color: ${colors.secondary.lightTeal} ${colors.background.light}; }
        .edit-modal-input:focus { border-color: ${colors.primary.teal} !important; }
      `}</style>

      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onMouseDown={(e) => { mouseDownOnOverlay.current = e.target === e.currentTarget; }}
        onClick={(e) => { if (e.target === e.currentTarget && mouseDownOnOverlay.current) onClose(); }}
      >
        <div style={{
          backgroundColor: colors.text.white,
          borderRadius: '0.75rem',
          width: '90%',
          maxWidth: '540px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }}>
          {/* Header */}
          <div style={{
            backgroundColor: colors.primary.teal,
            padding: '1rem 1.5rem',
            borderTopLeftRadius: '0.75rem',
            borderTopRightRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: colors.text.white,
              fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
            }}>
              Éditer la facture interne
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none',
                cursor: 'pointer', padding: '0.25rem',
                display: 'flex', alignItems: 'center',
                color: colors.text.white
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Contenu scrollable */}
          <div className="edit-modal-scroll" style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>

            {/* Bloc info (lecture seule) */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: colors.secondary.lightGray,
              borderRadius: '0.5rem'
            }}>
              <InfoRow label="N° facture" value={facture.numeroFacture || '—'} />
              <InfoRow label="Consultant" value={facture.consultantNom || '—'} />
              <InfoRow label="Période" value={[facture.trimestre, facture.annee].filter(Boolean).join(' ') || '—'} />
            </div>

            {/* Destinataire */}
            <FormField label="Destinataire" required>
              <select
                value={facture.destinataireType || 'ephyco'}
                onChange={(e) => onChange('destinataireType', e.target.value)}
                className="edit-modal-input"
                style={inputStyle}
              >
                <option value="ephyco">EPHYCO</option>
                <option value="prestataire">Autre prestataire</option>
              </select>
            </FormField>

            {facture.destinataireType === 'prestataire' && (
              <FormField label="Prestataire" required>
                <select
                  value={facture.prestataireId || ''}
                  onChange={(e) => onChange('prestataireId', e.target.value || null)}
                  required
                  className="edit-modal-input"
                  style={inputStyle}
                >
                  <option value="">Sélectionner un prestataire</option>
                  {prestataires?.map(p => (
                    <option key={p.id} value={p.id}>{p.fields?.Nom || 'Sans nom'}</option>
                  ))}
                </select>
              </FormField>
            )}

            {/* Intitulé */}
            <FormField label="Intitulé">
              <input
                type="text"
                value={facture.intitule || ''}
                onChange={(e) => onChange('intitule', e.target.value)}
                placeholder="Description (optionnel)"
                className="edit-modal-input"
                style={inputStyle}
              />
            </FormField>

            {/* Lignes de détail */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Lignes de détail</label>
              {lines.map((line, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => handleLineChange(i, 'description', e.target.value)}
                    placeholder="Description"
                    required
                    className="edit-modal-input"
                    style={{ ...inputStyle, flex: 3 }}
                  />
                  <input
                    type="number"
                    value={line.montant}
                    onChange={(e) => handleLineChange(i, 'montant', e.target.value)}
                    placeholder="Montant €"
                    required
                    min="0.01"
                    step="0.01"
                    className="edit-modal-input"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLine(i)}
                      style={{
                        background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: '1.25rem',
                        color: colors.text.secondary,
                        padding: '0 0.25rem', lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddLine}
                style={{
                  background: 'none',
                  border: `1px dashed ${colors.secondary.lightTeal}`,
                  borderRadius: '0.5rem',
                  padding: '0.4rem 0.75rem',
                  cursor: 'pointer',
                  color: colors.primary.teal,
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: '"Noto Sans", sans-serif'
                }}
              >
                + Ajouter une ligne
              </button>
            </div>

            {/* Taux TVA */}
            <FormField label="Taux de TVA">
              <select
                value={facture.tauxTVA}
                onChange={(e) => onChange('tauxTVA', parseFloat(e.target.value))}
                className="edit-modal-input"
                style={inputStyle}
              >
                <option value={0}>0% (Exonéré)</option>
                <option value={5.5}>5,5% (Taux réduit)</option>
                <option value={10}>10% (Taux intermédiaire)</option>
                <option value={20}>20% (Taux normal)</option>
              </select>
            </FormField>

            {/* Totaux */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: colors.secondary.lightGray,
              borderRadius: '0.5rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ color: colors.primary.gray }}>Montant HT :</span>
                <span style={{ color: colors.primary.gray, fontWeight: '600' }}>{formatEuro(montantHT)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                <span style={{ color: colors.primary.gray }}>TVA ({tauxTVA}%) :</span>
                <span style={{ color: colors.primary.gray, fontWeight: '600' }}>{formatEuro(montantTVA)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: `1px solid ${colors.secondary.mediumGray}` }}>
                <span style={{ fontWeight: 'bold', color: colors.primary.gray }}>Montant TTC :</span>
                <span style={{ color: colors.primary.teal, fontWeight: 'bold', fontSize: '1.125rem' }}>{formatEuro(montantTTC)}</span>
              </div>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <ActionButton
                text="Annuler"
                onClick={onClose}
                variant="ghost"
                style={{ backgroundColor: colors.secondary.mediumGray, color: colors.text.white }}
              />
              <ActionButton icon={Save} text="Enregistrer" onClick={onSave} variant="secondary" />
              {facture.statut === 'Brouillon' && onStatusChange && (
                <ActionButton
                  icon={Send}
                  text="Prêt à envoyer"
                  onClick={() => onStatusChange('A envoyer')}
                  variant="primary"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// === STYLES ===

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: `2px solid ${colors.secondary.lightTeal}`,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: '"Noto Sans", sans-serif',
  backgroundColor: colors.text.white
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  fontWeight: '600',
  color: colors.primary.gray,
  fontSize: '0.9375rem'
};

// === SOUS-COMPOSANTS ===

function FormField({ label, required, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={labelStyle}>{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
      <span style={{ fontWeight: '600', color: colors.primary.gray, fontSize: '0.875rem' }}>{label} :</span>
      <span style={{ color: colors.primary.teal, fontWeight: '600', fontSize: '0.875rem' }}>{value}</span>
    </div>
  );
}

export default EditFactureInterneModal;
